import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { TagSelector } from "@/components/tag-selector"
import { Loader2 } from "lucide-react"
import type { TagOption } from "@/types"

type FormData = {
  title: string
  description: string
  bpm: string
  key: string
  tag_ids: number[]
}

type Props = {
  data: FormData
  errors: Record<string, string[] | undefined>
  onChange: <K extends keyof FormData>(field: K, value: FormData[K]) => void
  availableKeys: string[]
  popularTags: TagOption[]
  customTags: string[]
  onCustomTagsChange: (tags: string[]) => void
  submitLabel: string
  processing: boolean
  onCancel: () => void
}

export function SampleFormFields({
  data,
  errors,
  onChange,
  availableKeys,
  popularTags,
  customTags,
  onCustomTagsChange,
  submitLabel,
  processing,
  onCancel,
}: Props) {
  return (
    <div className="space-y-6">
      <div>
        <Label htmlFor="title">Title</Label>
        <Input
          id="title"
          value={data.title}
          onChange={(e) => onChange("title", e.target.value)}
          placeholder="Give your sample a name"
          className="mt-1.5 font-mono"
          aria-invalid={!!errors.title}
        />
        {errors.title?.map((error, i) => (
          <p key={i} className="text-sm text-destructive mt-1">{error}</p>
        ))}
      </div>

      <div>
        <Label htmlFor="description">Description (optional)</Label>
        <Textarea
          id="description"
          value={data.description}
          onChange={(e) => onChange("description", e.target.value)}
          placeholder="Add some context about this sample..."
          className="mt-1.5 min-h-[100px]"
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="bpm">BPM (optional)</Label>
          <Input
            id="bpm"
            type="number"
            min="20"
            max="300"
            value={data.bpm}
            onChange={(e) => onChange("bpm", e.target.value)}
            placeholder="e.g. 140"
            className="mt-1.5 font-mono"
          />
        </div>

        <div>
          <Label htmlFor="key">Key (optional)</Label>
          <Select
            value={data.key || "none"}
            onValueChange={(value) => onChange("key", value === "none" ? "" : value)}
          >
            <SelectTrigger className="mt-1.5 font-mono">
              <SelectValue placeholder="Select key" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">Not specified</SelectItem>
              {availableKeys.map((key) => (
                <SelectItem key={key} value={key}>
                  {key}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <TagSelector
        popularTags={popularTags}
        selectedTagIds={data.tag_ids}
        customTags={customTags}
        onTagIdsChange={(ids) => onChange("tag_ids", ids)}
        onCustomTagsChange={onCustomTagsChange}
      />

      <div className="flex gap-3 pt-2">
        <Button
          type="submit"
          disabled={!data.title.trim() || processing}
          className="flex-1"
        >
          {processing && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
          {submitLabel}
        </Button>
        <Button type="button" variant="secondary" onClick={onCancel} disabled={processing}>
          Cancel
        </Button>
      </div>
    </div>
  )
}
